import { useEffect, useRef, useState } from "react";

export const CALENDAR_WEEK_COUNT = 6;
export const CALENDAR_WEEKDAY_HEADER_HEIGHT = 24;

const normalizeWeekCount = (weekCount) => {
  const normalizedWeekCount = Number(weekCount);

  return Number.isInteger(normalizedWeekCount) &&
    normalizedWeekCount >= 4 &&
    normalizedWeekCount <= 6
    ? normalizedWeekCount
    : CALENDAR_WEEK_COUNT;
};

export const getAdaptiveCalendarLayout = (
  containerHeight,
  weekCount = CALENDAR_WEEK_COUNT,
) => {
  const normalizedWeekCount = normalizeWeekCount(weekCount);
  const normalizedHeight = Number(containerHeight);
  const usableHeight =
    Number.isFinite(normalizedHeight) && normalizedHeight > 0
      ? Math.max(normalizedHeight - CALENDAR_WEEKDAY_HEADER_HEIGHT, 0)
      : 0;
  const rowHeight = usableHeight / normalizedWeekCount;

  if (rowHeight >= 88) {
    return {
      density: "spacious",
      rowHeight,
      visibleItemLimit: 3,
      weekCount: normalizedWeekCount,
    };
  }

  if (rowHeight >= 68) {
    return {
      density: "normal",
      rowHeight,
      visibleItemLimit: 2,
      weekCount: normalizedWeekCount,
    };
  }

  return {
    density: "compact",
    rowHeight,
    visibleItemLimit: 1,
    weekCount: normalizedWeekCount,
  };
};

const useAdaptiveCalendarLayout = (weekCount = CALENDAR_WEEK_COUNT) => {
  const calendarContainerRef = useRef(null);
  const [layout, setLayout] = useState(() =>
    getAdaptiveCalendarLayout(0, weekCount),
  );

  useEffect(() => {
    const calendarContainer = calendarContainerRef.current;

    if (!calendarContainer) return undefined;

    const updateLayout = (height) => {
      const nextLayout = getAdaptiveCalendarLayout(height, weekCount);

      setLayout((currentLayout) =>
        currentLayout.density === nextLayout.density &&
        currentLayout.weekCount === nextLayout.weekCount
          ? currentLayout
          : nextLayout,
      );
    };

    const measureCalendar = () => {
      updateLayout(calendarContainer.getBoundingClientRect().height);
    };

    measureCalendar();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureCalendar);

      return () => {
        window.removeEventListener("resize", measureCalendar);
      };
    }

    const observer = new ResizeObserver((entries) => {
      const calendarEntry = entries.find(
        (entry) => entry.target === calendarContainer,
      );

      updateLayout(
        calendarEntry?.contentRect?.height ??
          calendarContainer.getBoundingClientRect().height,
      );
    });

    observer.observe(calendarContainer);

    return () => {
      observer.disconnect();
    };
  }, [weekCount]);

  return {
    calendarContainerRef,
    ...layout,
  };
};

export default useAdaptiveCalendarLayout;
